import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import HoverVideo from './HoverVideo';
import { setTripHighlights } from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { httpsify } from '../utils/url';

const mmss = (s) => {
  const t = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Lazy hover-preview source for a clip: RED plays its CDN HLS proxy, others the
// presigned MP4. Mirrors the trip media card logic.
function useResolvePreview(asset) {
  const isRed = !!asset?.isRawVideo;
  const proxyReady = asset?.proxy?.status === 'ready';
  return useCallback(async () => {
    if (!asset) return null;
    if (isRed) return proxyReady && asset.proxyHlsUrl ? { url: httpsify(asset.proxyHlsUrl), hls: true } : null;
    if (!asset.s3Key) return null;
    const u = await getAssetPlaybackUrl(asset.s3Key);
    return u ? { url: u, hls: false } : null;
  }, [asset, isRed, proxyReady]);
}

function HighlightCard({ item, asset, tripId, editing, onStart, onRemove }) {
  const navigate = useNavigate();
  const resolve = useResolvePreview(asset);
  const isRed = !!asset.isRawVideo;
  const playable = !isRed || asset.proxy?.status === 'ready';
  const dur = Math.max(0, Math.round(asset.durationSec || 0)) || 60;
  const go = () => playable && navigate(`/watch/asset/${tripId}/${asset.id}`);
  return (
    <div className="cs-hl-card">
      <div role="button" tabIndex={0} onClick={go} onKeyDown={(e) => e.key === 'Enter' && go()} style={{ cursor: playable ? 'pointer' : 'default' }}>
        <HoverVideo
          className="cs-hl-cover"
          resolve={resolve}
          startSec={item.startSec || 0}
          disabled={!playable}
          style={
            asset.thumbUrl
              ? { backgroundImage: `url(${httpsify(asset.thumbUrl)})` }
              : { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 }
          }
        >
          {!asset.thumbUrl && (playable ? '▶' : '⏳')}
        </HoverVideo>
      </div>
      <div className="cs-hl-body">
        <div className="cs-hl-name" title={asset.name}>{asset.name}</div>
        {editing ? (
          <div className="cs-hl-edit">
            <label className="cs-hl-start">
              <span>Autoplay start: {mmss(item.startSec || 0)}</span>
              <input
                type="range"
                min="0"
                max={dur}
                step="0.5"
                value={Math.min(item.startSec || 0, dur)}
                onChange={(e) => onStart(Number(e.target.value))}
              />
            </label>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onRemove}>Remove</button>
          </div>
        ) : (
          (item.startSec || 0) > 0 && <div className="cs-hl-meta">preview from {mmss(item.startSec)}</div>
        )}
      </div>
    </div>
  );
}

function HighlightPicker({ assets, current, onClose, onAdd }) {
  const taken = new Set(current.map((h) => h.assetId));
  const videos = assets.filter((a) => a.kind !== 'photo' && !taken.has(a.id));
  return (
    <Modal title="Add a clip to highlights" onClose={onClose} maxWidth={920}>
      {videos.length === 0 ? (
        <div className="cs-empty">No more clips to add.</div>
      ) : (
        <div className="cs-pick-grid">
          {videos.map((a) => (
            <div className="cs-pick-tile" key={a.id}>
              <div className="cs-pick-thumb" style={a.thumbUrl ? { backgroundImage: `url(${httpsify(a.thumbUrl)})` } : {}}>
                {!a.thumbUrl && <span>▶</span>}
              </div>
              <div className="cs-pick-name" title={a.name}>{a.name}</div>
              <div className="cs-pick-actions">
                <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onAdd(a.id)}>Add</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// Curated "Highlights" strip: any trip clip can be added, each with its own
// hover-autoplay start time. Shown on the trip page below the magazine.
export default function TripHighlights({ trip, tripId, assets, editing = false }) {
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const highlights = useMemo(() => {
    const raw = Array.isArray(trip.highlights) ? trip.highlights : [];
    return raw
      .map((h) => ({ item: h, asset: assets.find((a) => a.id === h.assetId) }))
      .filter((x) => x.asset && x.asset.kind !== 'photo');
  }, [trip.highlights, assets]);

  const persist = (next, debounce = false) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const run = async () => {
      setSaving(true);
      try {
        await setTripHighlights(tripId, next);
      } finally {
        setSaving(false);
      }
    };
    if (debounce) saveTimer.current = setTimeout(run, 450);
    else run();
  };

  const currentList = () => (Array.isArray(trip.highlights) ? trip.highlights : []).map((h) => ({ ...h }));

  const add = (assetId) => {
    setPicking(false);
    persist([...currentList(), { assetId, startSec: 0 }]);
  };
  const remove = (assetId) => persist(currentList().filter((h) => h.assetId !== assetId));
  const setStart = (assetId, startSec) =>
    persist(currentList().map((h) => (h.assetId === assetId ? { ...h, startSec } : h)), true);

  if (!editing && highlights.length === 0) return null;

  return (
    <section className="cs-hl-wrap">
      <div className="cs-hl-head">
        <h2 className="cs-hl-title">Highlights</h2>
        {editing && <span className="cs-help" style={{ margin: 0 }}>Add any clip · drag the slider to set where its hover preview begins.</span>}
        {saving && <span className="cs-mag-saving">saving…</span>}
      </div>
      <div className="cs-hl-grid">
        {highlights.map(({ item, asset }) => (
          <HighlightCard
            key={item.assetId}
            item={item}
            asset={asset}
            tripId={tripId}
            editing={editing}
            onStart={(s) => setStart(item.assetId, s)}
            onRemove={() => remove(item.assetId)}
          />
        ))}
        {editing && (
          <button type="button" className="cs-hl-add" onClick={() => setPicking(true)}>
            <span className="cs-slot-plus">＋</span>
            <span className="cs-slot-hint">Add clip</span>
          </button>
        )}
      </div>
      {picking && (
        <HighlightPicker
          assets={assets}
          current={currentList()}
          onClose={() => setPicking(false)}
          onAdd={add}
        />
      )}
    </section>
  );
}
